import { useEffect, useRef, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { apiFetch } from '../lib/api.js'
import { getSession } from '../lib/session.js'
import { useQuestionFlow } from '../hooks/useQuestionFlow.js'
import SafeHtml from '../components/ui/SafeHtml.jsx'
import CircuitTrack from '../components/ui/CircuitTrack.jsx'
import TimerBar from '../components/ui/TimerBar.jsx'
import AnswerButton from '../components/ui/AnswerButton.jsx'
import FeedbackContent from '../components/ui/FeedbackContent.jsx'
import BonusSlot from '../components/ui/BonusSlot.jsx'
import BonusUseAnimation from '../components/ui/BonusUseAnimation.jsx'
import WandAnim from '../components/ui/WandAnim.jsx'
import SpeedFlame from '../components/ui/SpeedFlame.jsx'
import { TwemojiImg } from '../lib/twemoji.jsx'

export default function SoloGamePage() {
  const { code }   = useParams()
  const navigate   = useNavigate()
  const session    = getSession(code)

  const {
    kart, game, question, phase,
    selectedIdx, feedbackData,
    timerMin, timerMax, timeLeft,
    error, gamePaused,
    bonusAnim, setBonusAnim,
    bonusSlotMachine, answerHidden,
    setKart, setGamePaused, setError,
    handleSubmit,
    pausedRef, bonusJustUsedRef, submittingRef,
    timerRef, selectionTimerRef, autoAdvanceTimerRef,
    fetchNextQuestionRef, waitingForUnpauseRef,
  } = useQuestionFlow({
    code,
    session,
    onInitError:  () => navigate('/home', { replace: true }),
    onNotPlaying: () => navigate('/home', { replace: true }),
  })

  const nbCases  = game?.decks?.nb_cases       ?? 30
  const nbBonus  = game?.decks?.nb_cases_bonus ?? 6
  const imageUrl = game?.decks?.themes?.image_url ?? null

  const isGuest = game !== null && game.hote_id === null

  useEffect(() => {
    if (phase !== 'done' || game === null) return
    navigate(isGuest ? `/solo-stats/${code}` : `/stats/${code}`, { replace: true })
  }, [phase, code, navigate, isGuest, game])

  const reponses = question?.reponses ?? []
  const locked   = selectedIdx !== null
  const karts    = kart ? [kart] : []
  const effActif = kart?.effet_actif    ?? null
  const effExp   = kart?.effet_expire_le ?? null

  // ── Joueur fantôme ──────────────────────────────────────────────────────────
  const [ghostRecord, setGhostRecord]   = useState(null)  // { total_ms, replay }
  const [ghostKart, setGhostKart]       = useState(null)  // kart-like object pour CircuitTrack
  const [ghostStarted, setGhostStarted] = useState(false)
  const gameStartRef  = useRef(null)
  const ghostIdxRef   = useRef(0)

  // Chargement du replay fantôme dès que game est connu
  useEffect(() => {
    if (!game?.id) return
    apiFetch(`/api/games/${code}/solo-record`)
      .then(data => { if (data.replay?.length > 0) setGhostRecord(data) })
      .catch(() => {})
  }, [game?.id, code])

  // Marquer le démarrage de la course au premier passage en phase question
  useEffect(() => {
    if (phase === 'question' && !gameStartRef.current) {
      gameStartRef.current = Date.now()
      ghostIdxRef.current  = 0
      setGhostStarted(true)
    }
  }, [phase])

  // Animation du fantôme : avance selon les timestamps du replay
  useEffect(() => {
    if (!ghostRecord || !ghostStarted || phase === 'done') return
    const id = setInterval(() => {
      if (!gameStartRef.current) return
      const elapsed = Date.now() - gameStartRef.current
      const events  = ghostRecord.replay
      let idx = ghostIdxRef.current
      while (idx < events.length && events[idx].t <= elapsed) idx++
      ghostIdxRef.current = idx
      const activeEvent = idx > 0 ? events[idx - 1] : null
      setGhostKart(activeEvent ? {
        id:            'ghost',
        type_vehicule: kart?.type_vehicule ?? '👻',
        position_case: activeEvent.pos,
        tour_actuel:   activeEvent.tour,
        vitesse:       1,
      } : null)
    }, 200)
    return () => clearInterval(id)
  }, [ghostRecord, ghostStarted, phase === 'done', kart?.type_vehicule])
  // ────────────────────────────────────────────────────────────────────────────

  const [wandAnim, setWandAnim] = useState(false)
  const [bonusStartPos, setBonusStartPos] = useState(null)
  const desktopBonusRef = useRef(null)

  async function handleUseBonus() {
    if (!kart?.bonus_inventaire) return
    const bonusUsed = kart.bonus_inventaire

    // Compute start position from the visible BonusSlot (desktop only)
    let startPos = null
    if (window.innerWidth >= 1024 && desktopBonusRef.current) {
      const rect = desktopBonusRef.current.getBoundingClientRect()
      startPos = { x: rect.left + rect.width / 2, y: rect.top + rect.height / 2 }
    }
    setBonusStartPos(startPos)

    bonusJustUsedRef.current = true
    setKart(prev => prev ? { ...prev, bonus_inventaire: null } : prev)

    if (bonusUsed.id === 'wand') {
      clearInterval(timerRef.current)
      clearTimeout(selectionTimerRef.current)
      clearTimeout(autoAdvanceTimerRef.current)
      submittingRef.current = true
      setWandAnim(true)
      apiFetch(`/api/karts/${code}/use-bonus`, {
        method:  'POST',
        headers: { 'x-session-token': session.token },
        body:    JSON.stringify({ question_id: question?.id }),
      }).then(result => setKart(prev => prev ? { ...prev, ...result.kart } : result.kart))
        .catch(() => {})
        .finally(() => { bonusJustUsedRef.current = false })
      setTimeout(() => {
        setWandAnim(false)
        fetchNextQuestionRef.current?.(null, game)
      }, 1000)
      return
    }

    try {
      const result = await apiFetch(`/api/karts/${code}/use-bonus`, {
        method:  'POST',
        headers: { 'x-session-token': session.token },
      })
      setKart(prev => prev ? { ...prev, ...result.kart } : result.kart)
      setBonusAnim(bonusUsed.emoji)
    } catch (e) {
      bonusJustUsedRef.current = false
      setKart(prev => prev ? { ...prev, bonus_inventaire: bonusUsed } : prev)
      setError(e.message)
    } finally {
      setTimeout(() => { bonusJustUsedRef.current = false }, 3000)
    }
  }

  async function handlePause() {
    if (isGuest) {
      pausedRef.current            = true
      waitingForUnpauseRef.current = true
      setGamePaused(true)
      return
    }
    try {
      await apiFetch(`/api/games/${code}/pause`, { method: 'PUT' })
      pausedRef.current            = true
      waitingForUnpauseRef.current = true
      setGamePaused(true)
    } catch (e) {
      setError(e.message)
    }
  }

  async function handleResume() {
    if (isGuest) {
      pausedRef.current            = false
      waitingForUnpauseRef.current = false
      setGamePaused(false)
      fetchNextQuestionRef.current?.(kart, game)
      return
    }
    try {
      await apiFetch(`/api/games/${code}/resume`, { method: 'PUT' })
      pausedRef.current            = false
      waitingForUnpauseRef.current = false
      setGamePaused(false)
      fetchNextQuestionRef.current?.(kart, game)
    } catch (e) {
      setError(e.message)
    }
  }

  async function handleStop() {
    if (!isGuest) {
      try { await apiFetch(`/api/games/${code}`, { method: 'DELETE' }) } catch { /* ignore */ }
    }
    navigate(isGuest ? `/solo-stats/${code}` : `/stats/${code}`, { replace: true })
  }

  if (phase === 'loading' && !question && !gamePaused) {
    return (
      <div className="h-[100dvh] bg-gray-900 flex items-center justify-center">
        <div className="flex flex-col items-center gap-3">
          <svg className="animate-spin h-8 w-8 text-white/70" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z" />
          </svg>
          <p className="text-white/70 text-sm">Chargement…</p>
        </div>
      </div>
    )
  }

  // JSX partagé entre mobile et desktop — variable pour éviter de dupliquer le rendu
  const questionZone = (
    <>
      {(phase === 'loading') && (
        <div className="flex-1 flex items-center justify-center text-white/50">
          Chargement…
        </div>
      )}

      {(phase === 'question' || phase === 'feedback') && question && (
        <>
          <div className={phase !== 'question' ? 'invisible' : ''}>
            <div className="flex justify-between text-xs mb-1">
              <span className="text-white/40">Temps restant</span>
              <span className="text-white/40">{timeLeft}s</span>
            </div>
            <TimerBar timeLeft={timeLeft} timerMax={timerMax} />
          </div>

          <div className="flex-1 flex flex-col items-center justify-center px-2 min-h-0">
            {question.image_url && (
              <img src={question.image_url} alt="" className="w-full max-h-28 object-contain rounded-xl mb-2 mx-auto" />
            )}
            <p className="text-white text-base font-medium text-center leading-snug"><SafeHtml html={question.enonce} /></p>
          </div>

          <div className="shrink-0 relative pb-1">
            <div className={`space-y-2 [@media(max-height:700px)]:space-y-1 ${phase !== 'question' || answerHidden ? 'invisible' : ''}`}>
              {reponses.map((r, i) => {
                const state = i === selectedIdx ? 'selected' : null
                return (
                  <AnswerButton
                    key={i}
                    index={i}
                    text={r.texte ?? r}
                    state={state}
                    disabled={locked}
                    onClick={() => !locked && handleSubmit(i, question)}
                  />
                )
              })}
            </div>
            {phase === 'feedback' && feedbackData && (
              <div className="absolute inset-0 flex flex-col pb-1">
                <FeedbackContent
                  correcte={feedbackData.correcte}
                  isTimeout={feedbackData.isTimeout}
                  bonneReponseTexte={feedbackData.bonneReponseTexte}
                />
              </div>
            )}
          </div>
        </>
      )}

      {error && (
        <div className="text-orange-300 text-sm text-center bg-orange-500/20 rounded-xl py-2 px-3 flex flex-col items-center gap-2">
          <span>{/fetch/i.test(error) ? 'Connexion perdue' : error}</span>
          {/fetch/i.test(error) && (
            <button onClick={() => window.location.reload()} className="text-orange-200 underline font-medium">
              Reconnecter
            </button>
          )}
        </div>
      )}
    </>
  )

  const pauseButton = (
    <button
      onClick={handlePause}
      className="p-2 rounded-xl text-white/60 hover:text-white hover:bg-white/10 transition"
      aria-label="Mettre en pause"
    >
      <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
        <rect x="6" y="4" width="4" height="16" rx="1" />
        <rect x="14" y="4" width="4" height="16" rx="1" />
      </svg>
    </button>
  )

  return (
    <div className="h-[100dvh] bg-gray-900 overflow-hidden">

      {bonusAnim && <BonusUseAnimation emoji={bonusAnim} onDone={() => setBonusAnim(null)} startPos={bonusStartPos} />}
      {wandAnim  && <WandAnim onDone={() => setWandAnim(false)} startPos={bonusStartPos} />}

      {/* Overlay pause solo */}
      {gamePaused && (
        <div className="fixed inset-0 z-50 bg-gray-900/95 flex flex-col items-center justify-center gap-6">
          <div className="text-6xl">⏸</div>
          <h2 className="text-white text-2xl font-bold">Partie en pause</h2>
          <div className="flex flex-col gap-3 w-full max-w-xs px-4">
            <button onClick={handleResume} className="btn-primary py-3 text-lg font-semibold">
              ▶ Reprendre
            </button>
            <button
              onClick={handleStop}
              className="py-3 text-lg font-semibold text-white/70 border border-white/20 rounded-xl hover:bg-white/10 transition"
            >
              ■ Arrêter la partie
            </button>
          </div>
        </div>
      )}

      {/* ── MOBILE layout (< lg) ── */}
      <div className="flex flex-col h-full lg:hidden">

        <div className="shrink-0 h-52 [@media(max-height:600px)]:h-36 relative overflow-hidden">
          <CircuitTrack
            nbCases={nbCases}
            nbBonus={nbBonus}
            karts={karts}
            ghostKarts={ghostKart ? [ghostKart] : []}
            myKartId={kart?.id}
            pieges={[]}
            attackEvents={[]}
            imageUrl={imageUrl}
            className="absolute inset-0 w-full h-full"
          />
        </div>

        <main className="flex-1 min-h-0 overflow-hidden flex flex-col px-4 py-3 gap-3">
          {questionZone}
        </main>

        <div className="shrink-0 bg-black/40 flex items-center px-4 py-2 gap-3 border-t border-white/10">
          <BonusSlot
            bonus={kart?.bonus_inventaire ?? null}
            effetActif={effActif}
            effetExpireLe={effExp}
            frozen={false}
            onUse={handleUseBonus}
            slotMachine={bonusSlotMachine}
          />
          <div className="flex-1 flex justify-center">
            <button
              onClick={handlePause}
              className="p-2 rounded-xl text-white/60 hover:text-white active:scale-90 transition-all"
              aria-label="Mettre en pause"
            >
              <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
                <rect x="6" y="4" width="4" height="16" rx="1" />
                <rect x="14" y="4" width="4" height="16" rx="1" />
              </svg>
            </button>
          </div>
          <div className="shrink-0">
            {kart && <SpeedFlame vitesse={kart.vitesse ?? 1} />}
          </div>
        </div>
      </div>

      {/* ── DESKTOP layout (>= lg) ── */}
      <div className="hidden lg:flex h-full">
        <div className="flex-1 relative">
          <CircuitTrack
            nbCases={nbCases}
            nbBonus={nbBonus}
            karts={karts}
            ghostKarts={ghostKart ? [ghostKart] : []}
            myKartId={kart?.id}
            pieges={[]}
            attackEvents={[]}
            kartSize={24}
            autoPan={true}
            imageUrl={imageUrl}
            className="absolute inset-0 w-full h-full"
          />
        </div>

        <aside className="w-96 flex flex-col bg-gray-900/95 border-l border-white/10 overflow-hidden">
          <div className="shrink-0 flex items-center justify-between px-5 py-4 border-b border-white/10">
            <div className="flex items-center gap-2">
              {kart?.type_vehicule && <TwemojiImg emoji={kart.type_vehicule} size={28} />}
              <div>
                <div className="text-white font-semibold">{kart?.pseudo ?? '…'}</div>
              </div>
            </div>
            <div className="flex items-center gap-2">
              {kart && <SpeedFlame vitesse={kart.vitesse ?? 1} />}
              {pauseButton}
            </div>
          </div>

          <div className="flex-1 min-h-0 overflow-hidden flex flex-col px-5 py-4 gap-3">
            {questionZone}
          </div>

          <div className="shrink-0 flex justify-center px-5 py-4 border-t border-white/10">
            <div ref={desktopBonusRef}>
              <BonusSlot
                bonus={kart?.bonus_inventaire ?? null}
                effetActif={effActif}
                effetExpireLe={effExp}
                frozen={false}
                onUse={handleUseBonus}
                slotMachine={bonusSlotMachine}
              />
            </div>
          </div>
        </aside>
      </div>
    </div>
  )
}
